import ExponentialBackoffStrategy from "./ExponentialBackoffStrategy.js";
// src/lib/Backoff.ts
var Backoff = class Backoff {
	backoffStrategy_;
	maxNumberOfRetry_ = -1;
	backoffNumber_ = 0;
	backoffDelay_ = 0;
	timeoutID_ = -1;
	listeners_ = {
		ready: [],
		backoff: [],
		fail: []
	};
	/**
	* Constructs an exponential backoff (10, 20, 40, 80, etc.).
	*
	* @param options.randomisationFactor: defaults to 0, must be between 0 and 1
	* @param options.initialDelay: defaults to 100 ms
	* @param options.maxDelay: defaults to 10000 ms
	* @param options.factor: defaults to 2, must be greater than 1
	*
	* With these values, the backoff delay will increase from 100 ms to 10000 ms. The
	* randomisation factor controls the range of randomness and must be between 0
	* and 1. By default, no randomisation is applied on the backoff delay.
	*/
	static exponential(options) {
		return new Backoff(new ExponentialBackoffStrategy(options));
	}
	/**
	* Constructs a new backoff object from a specific backoff strategy. The backoff
	* strategy must implement the `BackoffStrategy`interface defined bellow.
	*
	* @param strategy: the backoff strategy to use
	*/
	constructor(backoffStrategy_) {
		this.backoffStrategy_ = backoffStrategy_;
	}
	/**
	* Starts a backoff operation. If provided, the error parameter will be emitted
	* as the last argument of the `backoff` and `fail` events to let the listeners
	* know why the backoff operation was attempted.
	*
	* An error will be thrown if a backoff operation is already in progress.
	*
	* In practice, this method should be called after a failed attempt to perform a
	* sensitive operation (connecting to a database, downloading a resource over the
	* network, etc.).
	*/
	backoff(err) {
		if (this.timeoutID_ !== -1) throw new Error("Backoff in progress.");
		if (this.backoffNumber_ === this.maxNumberOfRetry_) {
			this.emit("fail", err);
			this.reset();
		} else {
			this.backoffDelay_ = this.backoffStrategy_.next();
			this.timeoutID_ = setTimeout(this.onBackoff_.bind(this), this.backoffDelay_);
			this.emit("backoff", this.backoffNumber_, this.backoffDelay_, err);
		}
	}
	/**
	* Resets the backoff delay to the initial backoff delay and stop any backoff
	* operation in progress. After reset, a backoff instance can and should be
	* reused.
	*
	* In practice, this method should be called after having successfully completed
	* the sensitive operation guarded by the backoff instance or if the client code
	* request to stop any reconnection attempt.
	*/
	reset() {
		this.backoffNumber_ = 0;
		this.backoffStrategy_.reset();
		clearTimeout(this.timeoutID_);
		this.timeoutID_ = -1;
	}
	on(event, handler) {
		this.listeners_[event].push(handler);
	}
	emit(event, ...args) {
		for (const handler of this.listeners_[event]) handler(...args);
	}
	onBackoff_() {
		this.timeoutID_ = -1;
		this.emit("ready", this.backoffNumber_, this.backoffDelay_);
		this.backoffNumber_++;
	}
};
export { Backoff as default };

//# sourceMappingURL=Backoff.js.map